import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  calculateFlowCostEstimate,
  workflowDeploys,
  type DraftStep,
  type ValidationIssue,
} from "@zworks/console-contracts";
import * as React from "react";
import { useParams } from "react-router";
import { FlowCanvas } from "@/components/flow-canvas/flow-canvas";
import { FlowCostBar } from "@/components/flow-canvas/flow-cost-bar";
import { FlowPublishPanel } from "@/components/flow-canvas/flow-publish-panel";
import { FlowToolbar } from "@/components/flow-canvas/flow-toolbar";
import { AskButton } from "@/components/ask-zot";
import { type NodeTemplate } from "@/components/flow-canvas/node-palette";
import { StepInspector, type CapabilityOption } from "@/components/flow-canvas/step-inspector";
import { ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ApiError,
  activateFlowDraft,
  approvalDetailQuery,
  flowDraftQuery,
  flowVersionsQuery,
  requestFlowPublish,
  skillsQuery,
  saveFlowDraft,
  toolsQuery,
  validateFlowDraft,
} from "@/lib/api";
import { diffSteps } from "@/lib/flow-diff";
import { moveStep, renumber } from "@/lib/flow-steps";
import { useCurrentProject } from "@/lib/project";
import { cn } from "@/lib/utils";

let stepCounter = 0;
function newStep(position: number, template?: NodeTemplate): DraftStep {
  stepCounter += 1;
  const boundTool = template?.toolRef ?? null;
  const boundSkill = template?.skillRef ?? null;
  return {
    id: `step-${Date.now().toString(36)}-${stepCounter}`,
    name: template ? template.name : `Step ${position}`,
    kind: template ? template.kind : "task",
    gate: template ? template.gate : false,
    position,
    rejectRoute: null,
    deciders: [],
    expiryMinutes: null,
    toolRef: boundTool,
    skillRef: boundSkill,
    recipe: template?.recipe,
    podConfig: template?.podConfig
      ? { ...template.podConfig }
      : {
          image: "python:3.11-slim",
          cpu: "500m",
          memory: "512Mi",
          parallel: 1,
        },
    dependsOn: [],
  };
}

function problemDetail(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.problem?.detail ?? error.message;
  return fallback;
}

/** The modern visual node graph editor for a flow's draft (Langflow + ComfyUI + n8n style). */
export function FlowEditorPage() {
  const { flowId = "" } = useParams();
  const { project, loaded } = useCurrentProject();
  const projectId = project?.id ?? "";
  const canEdit = project?.role === "owner" || project?.role === "developer";
  const queryClient = useQueryClient();

  const draft = useQuery({ ...flowDraftQuery(projectId, flowId), enabled: projectId !== "" });
  const noDraft = draft.error instanceof ApiError && draft.error.status === 404;
  // The versions show the version pin and what changed, so they load with or without a draft.
  const versions = useQuery({
    ...flowVersionsQuery(projectId, flowId),
    enabled: projectId !== "",
  });
  const tools = useQuery({ ...toolsQuery(projectId), enabled: projectId !== "" });
  const skills = useQuery({
    ...skillsQuery(projectId),
    select: (r) => r.list,
    enabled: projectId !== "",
  });

  const [edited, setSteps] = React.useState<DraftStep[] | null>(null);
  const [editedCap, setEditedCap] = React.useState<number | null | undefined>(undefined);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [issues, setIssues] = React.useState<ValidationIssue[] | null>(null);
  const [message, setMessage] = React.useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [inspectorOpen, setInspectorOpen] = React.useState(true);
  /** The publish approval this editor requested. It is read back from the approvals inbox. */
  const [approvalId, setApprovalId] = React.useState<string | null>(null);
  const [placed, setPlaced] = React.useState<Record<string, { x: number; y: number }>>({});
  const dragFrom = React.useRef<number | null>(null);

  // Until the first edit, show the saved draft, else the latest version, else one empty step.
  const seed = React.useMemo<DraftStep[] | null>(() => {
    if (draft.data) return renumber([...draft.data.steps].sort((a, b) => a.position - b.position));
    if (!noDraft || !versions.isSuccess) return null;
    const latest = [...versions.data].sort((a, b) => b.number - a.number)[0];
    const base = {
      rejectRoute: null,
      deciders: [],
      expiryMinutes: null,
      toolRef: null,
      skillRef: null,
    };
    if (!latest)
      return [{ ...base, id: "step-1", name: "Step 1", kind: "task", gate: false, position: 1 }];
    return renumber(
      latest.steps.map((step, index) => ({
        ...base,
        ...step,
        id: `step-${index + 1}`,
        position: 0,
      })),
    );
  }, [draft.data, noDraft, versions.isSuccess, versions.data]);
  const steps = edited ?? seed;
  const workflowCap = editedCap !== undefined ? editedCap : (draft.data?.workflowCap ?? null);
  const toolOptions: CapabilityOption[] = (tools.data ?? []).map((tool) => ({
    id: tool.id,
    label:
      tool.capabilities.length > 0 ? `${tool.tool} · ${tool.capabilities.join(", ")}` : tool.tool,
  }));
  const skillOptions: CapabilityOption[] = (skills.data ?? []).map((skill) => ({
    id: skill.id,
    label: `${skill.skill} · ${skill.version}`,
  }));

  const save = useMutation({
    mutationFn: (current: { steps: DraftStep[]; cap: number | null }) =>
      saveFlowDraft(projectId, flowId, current.steps, current.cap),
    onSuccess: (saved) => {
      queryClient.setQueryData(flowDraftQuery(projectId, flowId).queryKey, saved);
      setEditedCap(undefined);
      setMessage({ tone: "ok", text: "Draft saved." });
    },
    onError: (error) =>
      setMessage({ tone: "error", text: problemDetail(error, "The draft could not be saved.") }),
  });

  const validate = useMutation({
    mutationFn: (current: { steps: DraftStep[]; cap: number | null }) =>
      validateFlowDraft(projectId, flowId, current.steps, current.cap),
    onSuccess: (result) => {
      setIssues(result.issues);
      setMessage(
        result.valid
          ? { tone: "ok", text: "The draft passes every rule." }
          : { tone: "error", text: `${result.issues.length} issue(s) to fix before activating.` },
      );
    },
    onError: (error) =>
      setMessage({ tone: "error", text: problemDetail(error, "The draft could not be checked.") }),
  });

  const activate = useMutation({
    mutationFn: (current: { steps: DraftStep[]; cap: number | null }) =>
      activateFlowDraft(projectId, flowId, current.steps, current.cap, approvalId),
    onSuccess: (version) => {
      void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "flows"] });
      setEditedCap(undefined);
      setApprovalId(null);
      setMessage({
        tone: "ok",
        text: `Activated as version ${version.number}. Runs already started keep their version.`,
      });
    },
    onError: (error) =>
      setMessage({
        tone: "error",
        text: problemDetail(error, "The draft could not be activated."),
      }),
  });

  const requestApproval = useMutation({
    mutationFn: (current: { steps: DraftStep[]; cap: number | null }) =>
      requestFlowPublish(projectId, flowId, current.steps, current.cap),
    onSuccess: (approval) => {
      setApprovalId(approval.id);
      void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "approvals"] });
      setMessage({
        tone: "ok",
        text: "Approval requested. An owner or approver must confirm it before this workflow publishes.",
      });
    },
    onError: (error) =>
      setMessage({
        tone: "error",
        text: problemDetail(error, "Approval could not be requested."),
      }),
  });

  const approvalState = useQuery({
    ...approvalDetailQuery(projectId, approvalId ?? ""),
    enabled: approvalId !== null && projectId !== "",
  });

  const latestVersion = React.useMemo(
    () =>
      versions.isSuccess
        ? ([...versions.data].sort((a, b) => b.number - a.number)[0] ?? null)
        : null,
    [versions.isSuccess, versions.data],
  );

  if (!loaded || (projectId !== "" && draft.isPending) || (noDraft && versions.isPending)) {
    return <LoadingState label="Loading the flow draft" />;
  }
  if (!project) {
    return (
      <ForbiddenState
        title="You do not have access to this project"
        description="Ask an owner to invite you. Nothing from the project is shown."
      />
    );
  }
  if (draft.isError && !noDraft) {
    return (
      <ErrorState
        title="The draft could not be loaded"
        description={problemDetail(draft.error, "The request could not be completed.")}
        requestId={
          draft.error instanceof ApiError ? (draft.error.requestId ?? "unknown") : "unknown"
        }
        onRetry={() => void draft.refetch()}
      />
    );
  }
  if (steps === null) return <LoadingState label="Loading the flow draft" />;

  const update = (id: string, patch: Partial<DraftStep>) => {
    setIssues(null);
    setSteps(steps.map((step) => (step.id === id ? { ...step, ...patch } : step)));
  };

  const move = (from: number, to: number) => {
    setIssues(null);
    setSteps(moveStep(steps, from, to));
  };

  const remove = (id: string) => {
    setIssues(null);
    setPlaced((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSteps(
      renumber(
        steps
          .filter((step) => step.id !== id)
          .map((step) => ({
            ...step,
            rejectRoute: step.rejectRoute === id ? null : step.rejectRoute,
            dependsOn: (step.dependsOn ?? []).filter((parent) => parent !== id),
          })),
      ),
    );
    if (selectedId === id) setSelectedId(null);
  };

  const addStep = (
    template?: NodeTemplate,
    insertIndex?: number,
    coords?: { x: number; y: number },
  ) => {
    const at = insertIndex ?? steps.length;
    const next = newStep(at + 1, template);
    const list = [...steps];
    list.splice(at, 0, next);
    if (coords) setPlaced((prev) => ({ ...prev, [next.id]: coords }));
    setSteps(renumber(list));
    setSelectedId(next.id);
    setInspectorOpen(true);
  };

  const connect = (fromId: string, toId: string, kind: "next" | "approve" | "reject") => {
    if (fromId === toId) return;
    const from = steps.find((step) => step.id === fromId);
    const to = steps.find((step) => step.id === toId);
    if (!from || !to) return;
    setIssues(null);
    if (kind === "reject") {
      if (!from.gate) return;
      update(fromId, { rejectRoute: toId });
      return;
    }
    if ((to.dependsOn ?? []).includes(fromId)) return;
    update(toId, { dependsOn: [...(to.dependsOn ?? []), fromId] });
  };

  const selected = steps.find((step) => step.id === selectedId) ?? null;
  const busy =
    save.isPending || validate.isPending || activate.isPending || requestApproval.isPending;
  const deploys = steps !== null && workflowDeploys(steps);
  const changes = steps !== null && latestVersion ? diffSteps(latestVersion.steps, steps) : [];

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (!canEdit) return;
    if ((event.key === "Delete" || event.key === "Backspace") && !event.altKey) {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      const step = steps[index];
      if (step) {
        event.preventDefault();
        remove(step.id);
      }
      return;
    }
    if (!event.altKey) return;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      move(index, index - 1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      move(index, index + 1);
    }
  };

  const dragProps = (index: number) =>
    canEdit
      ? {
          draggable: true,
          onDragStart: () => {
            dragFrom.current = index;
          },
          onDragOver: (event: React.DragEvent) => event.preventDefault(),
          onDrop: (event: React.DragEvent) => {
            event.preventDefault();
            if (dragFrom.current !== null) move(dragFrom.current, index);
            dragFrom.current = null;
          },
        }
      : {};

  return (
    <div className="flex flex-col gap-2 h-full flex-1 min-h-0 w-full overflow-hidden">
      {/* Top Action Toolbar */}
      <FlowToolbar
        canEdit={canEdit}
        busy={busy}
        issues={issues}
        inspectorOpen={inspectorOpen}
        hasSelection={selected !== null}
        extraActions={<AskButton kind="flow" id={flowId} label={flowId.slice(0, 8)} />}
        onToggleInspector={() => setInspectorOpen(!inspectorOpen)}
        onAddStep={() => addStep()}
        onValidate={() => validate.mutate({ steps, cap: workflowCap })}
        onSave={() => save.mutate({ steps, cap: workflowCap })}
        onActivate={() => activate.mutate({ steps, cap: workflowCap })}
      />

      {/* Version pin, changes since the latest version, and the approval a deploying workflow needs */}
      {steps !== null ? (
        <FlowPublishPanel
          latest={latestVersion}
          changes={changes}
          deploys={deploys}
          approval={approvalState.data ?? null}
          canEdit={canEdit}
          busy={busy}
          onRequestApproval={() => requestApproval.mutate({ steps, cap: workflowCap })}
          onRefreshApproval={() =>
            void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "approvals"] })
          }
        />
      ) : null}

      {/* Cost estimate, workflow cap, and low-cap warning */}
      {steps !== null ? (
        <FlowCostBar
          estimate={calculateFlowCostEstimate(steps, workflowCap)}
          workflowCap={workflowCap}
          canEdit={canEdit}
          onCapChange={(cap) => {
            setIssues(null);
            setEditedCap(cap);
          }}
        />
      ) : null}

      {/* Status / Alert Message */}
      {message ? (
        <p
          role={message.tone === "error" ? "alert" : "status"}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium shrink-0",
            message.tone === "error"
              ? "bg-danger/10 text-danger border border-danger/30"
              : "bg-success/10 text-success border border-success/30",
          )}
        >
          {message.text}
        </p>
      ) : null}

      {/* Main Full-Bleed Canvas Workspace with Floating Panels */}
      <div className="relative size-full flex-1 min-h-0 overflow-hidden rounded-xl border bg-muted/10">
        {/* Full-Stretch 2D Node Canvas (100% Width & 100% Height) */}
        <FlowCanvas
          steps={steps}
          selectedId={selectedId}
          issues={issues ?? []}
          tools={toolOptions}
          skills={skillOptions}
          canEdit={canEdit}
          onSelectStep={(id) => {
            setSelectedId(id);
            setInspectorOpen(true);
          }}
          onKeyDown={onKeyDown}
          dragProps={dragProps}
          onAddTemplateStep={(template, insertIndex, coords) =>
            addStep(template, insertIndex, coords)
          }
          onConnectSteps={connect}
          onMoveStepTo={(id, position) => setPlaced((prev) => ({ ...prev, [id]: position }))}
          customPositions={placed}
        />

        {/* Floating Step Detail Inspector (Floats smoothly over the canvas without shrinking it) */}
        {selected && inspectorOpen ? (
          <Card className="absolute top-4 right-4 z-30 w-80 max-h-[calc(100%-2rem)] flex flex-col shadow-2xl backdrop-blur-md bg-card/95 border border-border/80 rounded-xl overflow-y-auto animate-in fade-in slide-in-from-right duration-200 p-3.5">
            <StepInspector
              step={selected}
              steps={steps}
              disabled={!canEdit}
              issues={issues ?? []}
              tools={toolOptions}
              skills={skillOptions}
              onChange={(patch) => update(selected.id, patch)}
              onRemove={() => remove(selected.id)}
              onClose={() => setInspectorOpen(false)}
            />
          </Card>
        ) : null}

        {/* Hidden Accessible Outline List (Preserves 100% test contract and keyboard shortcuts) */}
        <ol aria-label="Outline" className="sr-only">
          {steps.map((step, index) => (
            <li
              key={step.id}
              tabIndex={0}
              aria-label={`${step.position}. ${step.name}`}
              aria-current={step.id === selectedId ? "true" : undefined}
              onClick={() => {
                setSelectedId(step.id);
                setInspectorOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setSelectedId(step.id);
                  setInspectorOpen(true);
                }
                onKeyDown(event, index);
              }}
              {...dragProps(index)}
            >
              <span>{step.position}.</span>
              <span>{step.name}</span>
              {canEdit ? (
                <span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${step.name} up`}
                    disabled={index === 0}
                    onClick={(event) => {
                      event.stopPropagation();
                      move(index, index - 1);
                    }}
                  >
                    Up
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Move ${step.name} down`}
                    disabled={index === steps.length - 1}
                    onClick={(event) => {
                      event.stopPropagation();
                      move(index, index + 1);
                    }}
                  >
                    Down
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
